import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import MobileStickyCTA from '@/components/feature/MobileStickyCTA';
import { useSEO } from '../../utils/seo';
import { usePharmacy } from '@/context/PharmacyContext';

const serviceAreas = ['Thornhill', 'North York', 'Richmond Hill', 'Vaughan', 'Toronto'];

function formatTime(hhmm: string): string {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${hour}:${String(m).padStart(2, '0')} ${period}`;
}

export default function LocationPage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const { settings } = usePharmacy();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const pharmacyName = settings?.pharmacy_name || 'Thornhill Drug-Mart';
  const phone = settings?.phone || '(905) 709-9985';
  const phoneRaw = settings?.phone_raw || '9057099985';
  const fax = settings?.fax || '(905) 709-9986';
  const email = settings?.email || 'info@thornhilltreatment.com';
  const fullAddress = settings
    ? `${settings.address_line1}, ${settings.city}, ${settings.province} ${settings.postal_code}`
    : '8108 Yonge St Unit 3-4, Thornhill, ON L4J 1W4';
  const mapsDirectionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(fullAddress)}`;
  const doctorSchedule = settings?.doctor_schedule || [];
  const businessHours = settings?.business_hours || [];

  // Build day-by-day doctor view from DoctorEntry[] format
  const DAYS_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const doctorScheduleByDay = DAYS_ORDER.map((day) => {
    const dayDoctors = doctorSchedule
      .filter((doc: any) => doc.schedule?.some((s: any) => s.day === day))
      .map((doc: any) => {
        const dayEntry = doc.schedule.find((s: any) => s.day === day);
        return {
          name: doc.name,
          open: dayEntry?.open || '',
          close: dayEntry?.close || '',
        };
      })
      .sort((a: any, b: any) => a.open.localeCompare(b.open));
    return { day, doctors: dayDoctors };
  });

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://thornhilltreatment.com';

  useSEO({
    title: `${pharmacyName} Location & Hours | ${settings?.address_line1?.split(' ').slice(0, 2).join(' ') || '8108 Yonge St'}`,
    description:
      `Visit ${pharmacyName} at ${fullAddress}. Open 7 days a week. Free parking available. Call ${phone} for directions.`,
    keywords:
      `${settings?.city || 'Thornhill'} pharmacy location, pharmacy hours, ${pharmacyName}, pharmacy directions, pharmacy near me`,
    canonical: `${siteUrl}/location`,
    ogType: 'website',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Pharmacy',
      name: pharmacyName,
      description: `Professional pharmacy providing addiction treatment services and pharmacy care in ${settings?.city || 'Thornhill'}, Ontario`,
      url: `${siteUrl}/location`,
      telephone: `+1-${phoneRaw}`,
      address: {
        '@type': 'PostalAddress',
        streetAddress: settings?.address_line1 || '8108 Yonge St Unit 3-4',
        addressLocality: settings?.city || 'Thornhill',
        addressRegion: settings?.province || 'ON',
        postalCode: settings?.postal_code || 'L4J 1W4',
        addressCountry: 'CA',
      },
      geo: { '@type': 'GeoCoordinates', latitude: settings?.latitude || '43.8184', longitude: settings?.longitude || '-79.4278' },
    },
  });

  const getCurrentDay = () => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[new Date().getDay()];
  };
  const currentDay = getCurrentDay();

  return (
    <div className="min-h-screen bg-background-100">
      <Navbar isScrolled={isScrolled} />

      {/* ── Hero Banner ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '320px' }}>
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=modern%20professional%20pharmacy%20interior%20clean%20organized%20prescription%20counter%20warm%20teal%20white%20tones%20bright%20lighting%20welcoming%20healthcare%20environment%20wide%20angle%20professional%20medical%20facility%20Thornhill%20Ontario&width=1920&height=400&seq=loc-hero-v3&orientation=landscape"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
            width={1920}
            height={400}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-900/90 via-foreground-900/75 to-foreground-900/60" />
        </div>

        <div
          className="relative z-10 max-w-7xl mx-auto px-6 flex flex-col justify-center"
          style={{ minHeight: '320px', paddingTop: '128px', paddingBottom: '48px' }}
        >
          <nav aria-label="Breadcrumb" className="mb-5">
            <ol className="flex items-center space-x-2 text-sm">
              <li><Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line text-white/50"></i></li>
              <li><span className="text-primary-400 font-medium">Location &amp; Hours</span></li>
            </ol>
          </nav>

          <motion.h1
            initial={{ opacity: 0, y: 28 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight max-w-2xl"
          >
            Location &amp; Hours
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: 'easeOut' }}
            className="mt-4 text-lg text-white/80 max-w-xl"
          >
            {settings?.address_line1 || '8108 Yonge St Unit 3-4'}, {settings?.city || 'Thornhill'}, {settings?.province || 'ON'} — open 7 days a week
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3, ease: 'easeOut' }}
            className="mt-8 flex flex-wrap gap-4"
          >
            <a
              href={mapsDirectionsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white font-semibold px-7 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-navigation-line"></i>
              Get Directions
            </a>
            <a
              href={`tel:${phoneRaw}`}
              className="inline-flex items-center gap-2 border border-white/40 hover:border-white text-white font-semibold px-7 py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-phone-line"></i>
              {phone}
            </a>
          </motion.div>
        </div>
      </section>

      {/* ── Welcome to Our Location ── */}
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-6">
        <div className="text-center mb-10">
          <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-3">
            Your Neighborhood Pharmacy
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-foreground-950 mb-5">
            Welcome to Our <strong>{settings?.city || 'Thornhill'}</strong> Location
          </h2>
        </div>
        <div className="max-w-4xl mx-auto space-y-5 text-base leading-relaxed text-foreground-700">
          <p>
            Conveniently situated at <strong>{settings?.address_line1 || '8108 Yonge Street, Unit 3-4'}</strong>, {pharmacyName} is located in the vibrant Yonge Street corridor — one of the most accessible commercial and healthcare hubs in York Region. Our <a href="/pharmacy-services" className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors font-semibold">pharmacy</a> and <a href="/addiction-services" className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors font-semibold">addiction clinic</a> serves patients from across the Greater Toronto Area, including <strong>Richmond Hill</strong>, <strong>Vaughan</strong>, <strong>North York</strong>, <strong>Markham</strong>, and beyond.
          </p>
          <p>
            The surrounding neighborhood offers a mix of medical offices, retail shops, and dining options, making it easy to combine your pharmacy visit with other errands. We are just minutes from Highway 407 and Highway 7, with ample free on-site parking directly in front of our unit. For those using public transit, multiple YRT and Viva bus routes stop within a short walking distance along Yonge Street, and the Finch TTC subway station is a quick bus ride south.
          </p>
          <p>
            Our facility is fully wheelchair accessible with ground-level entry, wide doorways, and accessible washrooms. Whether you are picking up a prescription, attending a scheduled <a href="/methadone-treatment" className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors font-semibold">methadone</a> or <a href="/suboxone-treatment" className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors font-semibold">Suboxone</a> dosing appointment, or stopping by for a <a href="/naloxone-kits" className="text-primary-600 hover:text-primary-700 underline underline-offset-2 transition-colors font-semibold">free naloxone kit</a>, our team is ready to welcome you in a safe, private, and professional environment.
          </p>
        </div>
      </section>

      {/* ── Main 2-column layout ── */}
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex flex-col lg:flex-row gap-12 items-start">

          {/* ── Left: Main Content ── */}
          <main className="flex-1 min-w-0 space-y-10">

            {/* Contact Info Cards */}
            <section aria-labelledby="contact-cards-heading">
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-4">
                Contact Information
              </p>
              <h2 id="contact-cards-heading" className="text-2xl font-bold text-foreground-950 mb-6">
                Reach Us Anytime
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { icon: 'ri-map-pin-line',  title: 'Address', lines: [settings?.address_line1 || '', `${settings?.city || ''}, ${settings?.province || ''} ${settings?.postal_code || ''}`, settings?.country || ''] },
                  { icon: 'ri-phone-line',    title: 'Phone',   lines: [phone] },
                  { icon: 'ri-printer-line',  title: 'Fax',     lines: [fax] },
                  { icon: 'ri-mail-line',     title: 'Email',   lines: [email] },
                ].map((card) => (
                  <div key={card.title} className="bg-background-50 border border-background-200 rounded-lg p-5 flex items-start gap-4">
                    <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${card.icon} text-primary-600 text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-1">{card.title}</p>
                      {card.title === 'Phone' ? (
                        <a href={`tel:${phoneRaw}`} className="text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer">{card.lines[0]}</a>
                      ) : card.title === 'Email' ? (
                        <a href={`mailto:${email}`} className="text-sm font-semibold text-primary-600 hover:text-primary-700 cursor-pointer break-all">{card.lines[0]}</a>
                      ) : (
                        card.lines.map((l: string, i: number) => (
                          <p key={i} className="text-sm text-foreground-700 leading-snug">{l}</p>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Map */}
            <section aria-labelledby="map-heading">
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-2">
                Find Us
              </p>
              <h2 id="map-heading" className="text-2xl font-bold text-foreground-950 mb-5">
                Our Location
              </h2>
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <div className="aspect-video w-full">
                  <iframe
                    src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2879.8!2d${settings?.longitude || '-79.4278'}!3d${settings?.latitude || '43.8184'}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2z${settings?.latitude || '43'}!5e0!3m2!1sen!2sca!4v1700000000000`}
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={`${pharmacyName} Location`}
                  ></iframe>
                </div>
                <div className="px-6 py-4 border-t border-background-200/70 flex flex-col sm:flex-row gap-3">
                  <a
                    href={mapsDirectionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-navigation-line"></i>
                    Get Directions
                  </a>
                  <a
                    href={`tel:${phoneRaw}`}
                    className="flex-1 inline-flex items-center justify-center gap-2 border border-background-200 hover:border-primary-500 text-foreground-700 hover:text-primary-600 text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line"></i>
                    Call Now
                  </a>
                </div>
              </div>
            </section>

            {/* Business Hours Section */}
            <section aria-labelledby="hours-heading">
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-2">
                Business Hours
              </p>
              <h2 id="hours-heading" className="text-2xl font-bold text-foreground-950 mb-5">
                Operating Hours
              </h2>

              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden divide-y divide-background-200/70">
                {businessHours.map((item: any) => {
                  const isToday = currentDay === item.day;
                  return (
                    <div
                      key={item.day}
                      className={`flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 px-5 py-4 transition-colors ${isToday ? 'bg-primary-50' : 'hover:bg-background-100'}`}
                    >
                      <div className="flex items-center gap-3 w-36 flex-shrink-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isToday ? 'bg-primary-500' : 'bg-foreground-300'}`}></div>
                        <span className={`text-sm font-bold ${isToday ? 'text-primary-700' : 'text-foreground-900'}`}>
                          {item.day}
                          {isToday && (
                            <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">Today</span>
                          )}
                        </span>
                      </div>
                      <div className="text-sm text-foreground-600 flex-1 flex items-center gap-1.5">
                        <i className="ri-time-line text-primary-500 text-xs"></i>
                        {item.hours}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Doctor Schedule */}
            {doctorSchedule.length > 0 && (
              <section aria-labelledby="schedule-heading">
                <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-2">
                  Doctor Schedule
                </p>
                <h2 id="schedule-heading" className="text-2xl font-bold text-foreground-950 mb-5">
                  Weekly Doctor Availability
                </h2>

                <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden divide-y divide-background-200/70">
                  {doctorScheduleByDay.map(({ day, doctors }) => {
                    const isToday = currentDay === day;
                    const hasDoctors = doctors.length > 0;

                    return (
                      <div
                        key={day}
                        className={`transition-colors ${isToday ? 'bg-primary-50' : 'hover:bg-background-100'}`}
                      >
                        {/* Day row header */}
                        <div className="flex items-center gap-3 px-5 py-3.5">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${isToday ? 'bg-primary-500' : 'bg-foreground-300'}`}></div>
                          <span className={`text-sm font-bold w-28 flex-shrink-0 ${isToday ? 'text-primary-700' : 'text-foreground-900'}`}>
                            {day}
                            {isToday && (
                              <span className="ml-2 text-xs bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full font-medium">Today</span>
                            )}
                          </span>

                          {hasDoctors ? (
                            <div className="flex flex-wrap gap-3 flex-1">
                              {doctors.map((doc: any) => (
                                <div
                                  key={doc.name}
                                  className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-lg px-3 py-2"
                                >
                                  <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                                    <i className="ri-user-3-line text-primary-600 text-xs"></i>
                                  </div>
                                  <div>
                                    <p className="text-xs font-semibold text-foreground-900 leading-tight">{doc.name}</p>
                                    <p className="text-[11px] text-foreground-500 leading-tight">
                                      {formatTime(doc.open)} – {formatTime(doc.close)}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <span className="text-sm text-foreground-400 italic">No Doctor Available</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 bg-background-50 border border-primary-200 rounded-lg px-5 py-4 flex items-start gap-3">
                  <div className="w-8 h-8 bg-primary-500 rounded-md flex items-center justify-center flex-shrink-0">
                    <i className="ri-information-line text-white text-base"></i>
                  </div>
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    Doctor schedules may vary. Please call ahead at{' '}
                    <a href={`tel:${phoneRaw}`} className="text-primary-600 hover:text-primary-700 font-semibold cursor-pointer">{phone}</a>{' '}
                    to confirm availability and book your appointment.
                  </p>
                </div>
              </section>
            )}

            {/* Parking & Accessibility */}
            <section aria-labelledby="access-heading">
              <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-2">
                Accessibility
              </p>
              <h2 id="access-heading" className="text-2xl font-bold text-foreground-950 mb-5">
                Parking &amp; Access
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { icon: 'ri-parking-box-line', title: 'Free Parking',          desc: 'Ample free parking on-site for all visitors.' },
                  { icon: 'ri-wheelchair-line',  title: 'Wheelchair Accessible', desc: 'Fully accessible facility with ramps and elevators.' },
                  { icon: 'ri-bus-line',          title: 'Public Transit',         desc: 'Conveniently located near major bus routes on Yonge St.' },
                ].map((item) => (
                  <div key={item.title} className="bg-background-50 border border-background-200 rounded-lg p-5">
                    <div className="w-10 h-10 bg-primary-50 rounded-lg flex items-center justify-center mb-3">
                      <i className={`${item.icon} text-primary-600 text-xl`}></i>
                    </div>
                    <h3 className="text-sm font-bold text-foreground-950 mb-1">{item.title}</h3>
                    <p className="text-xs text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </section>
          </main>

          {/* ── Right: Sticky Sidebar ── */}
          <aside className="sticky top-24 space-y-5 w-full lg:w-72 flex-shrink-0">

            {/* Areas We Serve */}
            <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              <div className="bg-primary-900 px-5 py-4">
                <h2 className="text-xs font-bold text-white tracking-[0.12em] uppercase">Areas We Serve</h2>
              </div>
              <ul className="divide-y divide-background-200/70">
                {serviceAreas.map((area) => (
                  <li key={area} className="flex items-center justify-between px-5 py-3 text-sm text-foreground-600 font-medium hover:bg-background-100 transition-colors">
                    <span className="flex items-center gap-2">
                      <i className="ri-map-pin-2-fill text-primary-500 text-xs"></i>
                      {area}
                    </span>
                    <i className="ri-arrow-right-line text-foreground-300 text-sm"></i>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quick Contact */}
            <div className="bg-primary-900 rounded-lg p-6 text-white">
              <p className="text-sm font-semibold mb-1 text-white">Need to reach us?</p>
              <p className="text-xs text-foreground-400 mb-5 leading-relaxed">
                Our team is available 7 days a week to help you.
              </p>
              <a
                href={`tel:${phoneRaw}`}
                className="flex items-center gap-2.5 text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer mb-3"
              >
                <i className="ri-phone-line text-primary-400"></i>
                {phone}
              </a>
              <a
                href={`mailto:${email}`}
                className="flex items-center gap-2.5 text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer mb-3"
              >
                <i className="ri-mail-line text-primary-400"></i>
                {email}
              </a>
              <a
                href={mapsDirectionsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2.5 text-sm text-foreground-300 hover:text-white transition-colors cursor-pointer"
              >
                <i className="ri-navigation-line text-primary-400"></i>
                Get Directions
              </a>
            </div>

            {/* CTA Card */}
            <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
              <div className="w-full h-36 overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=welcoming%20pharmacy%20storefront%20exterior%20modern%20building%20Thornhill%20Ontario%20professional%20healthcare%20facility%20bright%20daytime%20clean%20signage%20accessible%20entrance%20inviting&width=400&height=200&seq=loc-sidebar-cta-v2&orientation=landscape"
                  alt={`${pharmacyName} exterior`}
                  className="w-full h-full object-cover object-top"
                  width={400}
                  height={160}
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <h3 className="text-sm font-semibold text-foreground-950 mb-1">Walk-Ins Welcome</h3>
                <p className="text-xs text-foreground-500 mb-4 leading-relaxed">
                  No appointment needed — come in anytime during operating hours.
                </p>
                <a
                  href={`tel:${phoneRaw}`}
                  className="block text-center bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  Call Ahead
                </a>
              </div>
            </div>

          </aside>
        </div>
      </div>

      {/* ── Planning Your Visit ── */}
      <section className="max-w-7xl mx-auto px-6 pb-14">
        <div className="bg-background-100 rounded-2xl p-8 sm:p-12">
          <div className="text-center mb-8">
            <p className="text-xs font-semibold text-primary-600 uppercase tracking-[0.15em] mb-3">
              Before You Arrive
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground-950 mb-4">
              Planning Your <strong>Visit</strong>
            </h2>
            <p className="text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
              Everything you need to know before coming to our <strong>{settings?.city || 'Thornhill'} pharmacy and addiction clinic</strong>
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div>
              <h3 className="text-lg font-bold text-foreground-950 mb-3">What to Bring</h3>
              <ul className="space-y-2 text-sm text-foreground-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-primary-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>Your Ontario Health Card (if you have one) — though not required for naloxone kits or many walk-in services</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-primary-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>Any current prescription bottles or medication lists from your doctor</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-primary-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>Your private insurance card or drug benefit information for direct billing</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-primary-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>A list of questions or concerns you would like to discuss with the pharmacist or doctor</span>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-bold text-foreground-950 mb-3">What to Expect</h3>
              <ul className="space-y-2 text-sm text-foreground-700">
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-primary-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>Friendly staff will greet you at reception and guide you through the check-in process</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-primary-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>Private consultation rooms are available for confidential conversations with our pharmacists and doctors</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-primary-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>Typical wait times are under 15 minutes for prescription pickup and under 30 minutes for consultations</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-checkbox-circle-fill text-primary-600 mr-2 mt-0.5 flex-shrink-0"></i>
                  <span>We accept walk-ins for most services — no appointment needed for prescriptions, naloxone kits, or pharmacy consultations</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="mt-8 text-center">
            <p className="text-sm text-foreground-500 max-w-xl mx-auto leading-relaxed">
              Have questions before you arrive? Call us at{' '}
              <a href={`tel:${phoneRaw}`} className="text-primary-600 font-semibold hover:text-primary-700 cursor-pointer">{phone}</a>
              {' '}and our team will be happy to assist. We look forward to welcoming you to <strong>{pharmacyName}</strong>.
            </p>
          </div>
        </div>
      </section>

      <MobileStickyCTA />
      <Footer />
    </div>
  );
}